import { useContext, useEffect } from "react";
import { AuthContext } from "context/context";
import { EndosoContext } from "pages/endoso/context";
import { endoso } from "services";
import { TAMANIO_PAGINA_RECIBOS } from "shared/utils/endosoTypes";

/**
 * Endoso 25 (HU6, iteración 2): paginación server-side de
 * poliza/lista-recibos y selección de recibos a refacturar.
 *
 * "Seleccionar todos" solo cubre la página actual (decisión de producto): el
 * backend no expone un flag ni un endpoint para el universo completo. Lo ya
 * elegido en otras páginas se conserva porque recibosSeleccionados vive en
 * EndosoContext y es acumulativo entre páginas.
 */
export const usePaginacionRecibos = () => {
  const { setTimer } = useContext(AuthContext);
  const {
    formDta,
    createMessage,
    recibos,
    setRecibos,
    paginaRecibos,
    setPaginaRecibos,
    totalPaginasRecibos,
    setTotalPaginasRecibos,
    totalRecibos,
    loaderRecibos,
    setLoaderRecibos,
    recibosSeleccionados,
    setRecibosSeleccionados,
  } = useContext(EndosoContext);

  const cargarPagina = async (page) => {
    setTimer(0);
    setLoaderRecibos(true);
    const response = await endoso.listaRecibos(
      formDta.noSolicitud,
      page,
      TAMANIO_PAGINA_RECIBOS
    );
    if (response?.data?.codigo === 1) {
      setRecibos(response.data.recibos ?? []);
      setPaginaRecibos(response.data.paginaActual ?? page);
      setTotalPaginasRecibos(response.data.totalPaginas ?? 0);
    } else {
      createMessage({
        text: "OCURRIO UN PROBLEMA CON SU SOLICITUD",
        variant: "warning",
      });
    }
    setLoaderRecibos(false);
  };

  useEffect(() => {
    cargarPagina(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estaSeleccionado = (uuid) =>
    recibosSeleccionados.some((recibo) => recibo.uuid === uuid);

  const alternarRecibo = (row) => {
    if (estaSeleccionado(row.uuid)) {
      setRecibosSeleccionados(
        recibosSeleccionados.filter((recibo) => recibo.uuid !== row.uuid)
      );
    } else {
      setRecibosSeleccionados([...recibosSeleccionados, row]);
    }
  };

  const todosPaginaSeleccionados =
    recibos.length > 0 && recibos.every((row) => estaSeleccionado(row.uuid));
  const algunoPaginaSeleccionado =
    !todosPaginaSeleccionados &&
    recibos.some((row) => estaSeleccionado(row.uuid));

  const seleccionarPagina = () => {
    if (todosPaginaSeleccionados) {
      const uuidsPagina = recibos.map((row) => row.uuid);
      setRecibosSeleccionados(
        recibosSeleccionados.filter(
          (recibo) => !uuidsPagina.includes(recibo.uuid)
        )
      );
    } else {
      const nuevos = recibos.filter((row) => !estaSeleccionado(row.uuid));
      setRecibosSeleccionados([...recibosSeleccionados, ...nuevos]);
    }
  };

  const rowCount =
    totalRecibos > 0
      ? totalRecibos
      : totalPaginasRecibos * TAMANIO_PAGINA_RECIBOS;

  return {
    recibos,
    paginaRecibos,
    totalPaginasRecibos,
    loaderRecibos,
    rowCount,
    recibosSeleccionados,
    cargarPagina,
    alternarRecibo,
    estaSeleccionado,
    seleccionarPagina,
    todosPaginaSeleccionados,
    algunoPaginaSeleccionado,
  };
};
